/**
 * TanStack Query hooks for dance data
 */

import { useQuery, useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  danceApi,
  type Dance,
  type CreateDanceRequest,
  type GenerateDanceRequest,
  type GenerateDanceResponse,
  type DanceSource
} from '../api/danceApi';

// Query keys
export const danceKeys = {
  all: ['dances'] as const,
  lists: () => [...danceKeys.all, 'list'] as const,
  list: (source?: DanceSource | 'ALL') => [...danceKeys.lists(), source] as const,
  details: () => [...danceKeys.all, 'detail'] as const,
  detail: (id: string) => [...danceKeys.details(), id] as const,
};

/**
 * Hook to fetch a list of dances
 */
export function useDances(source?: DanceSource | 'ALL', limit?: number) {
  return useQuery({
    queryKey: danceKeys.list(source),
    queryFn: () => danceApi.listDances({ source, limit }),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch a single dance by ID
 */
export function useDance(danceId: string | undefined) {
  return useQuery({
    queryKey: danceKeys.detail(danceId || ''),
    queryFn: () => danceApi.getDance(danceId!),
    enabled: !!danceId, // Only fetch if danceId is provided
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to create a manual dance
 */
export function useCreateDance(): UseMutationResult<Dance, Error, CreateDanceRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDanceRequest) => danceApi.createDance(data),
    onSuccess: (dance) => {
      // Update the detail cache
      queryClient.setQueryData(danceKeys.detail(dance.danceId), dance);

      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: danceKeys.lists() });

      // Track with Datadog RUM
      if (window.DD_RUM) {
        window.DD_RUM.addAction('dance_uploaded', {
          source: 'manual',
          danceId: dance.danceId,
          title: dance.title
        });
      }
    },
    onError: (error) => {
      // Track errors with Datadog RUM
      if (window.DD_RUM) {
        window.DD_RUM.addAction('dance_upload_failed', {
          source: 'manual',
          error: error.message
        });
      }
    }
  });
}

/**
 * Hook to generate a dance using AI
 */
export function useGenerateDance(): UseMutationResult<GenerateDanceResponse, Error, GenerateDanceRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateDanceRequest) => danceApi.generateDance(data),
    onSuccess: (response) => {
      const { dance, tokensUsed, generationTimeMs } = response;

      // Update the detail cache
      queryClient.setQueryData(danceKeys.detail(dance.danceId), dance);

      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: danceKeys.lists() });

      // Track with Datadog RUM
      if (window.DD_RUM) {
        window.DD_RUM.addAction('dance_generated', {
          source: 'ai',
          danceId: dance.danceId,
          title: dance.title,
          tokensUsed,
          generationTimeMs,
          success: true
        });
      }
    },
    onError: (error) => {
      // Track errors with Datadog RUM
      if (window.DD_RUM) {
        window.DD_RUM.addAction('dance_generation_failed', {
          source: 'ai',
          error: error.message
        });
      }
    }
  });
}
